/* System defined core library */
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { MetricApiService } from '../shared/services/metric-api.service';
import { RoutingService } from '../../shared/services/routing.service';
import { UtilityService } from '../../shared/services/utility.service';
import { AppointmentService } from '../shared/services/appointment/appointment.service';
import { SelectedExamDetailService } from '../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../shared/models/scheduling/appointmentdetail.model';

/* User defined enums/constants */
import { ComponentType, LogLevel } from '../../shared/enums/scheduling.enum';
import { SchedulingApiService } from '../shared/services/scheduling-api.service';
import { BatteryConfig } from '../../shared/models/scheduling/batteryConfig.model';
import { voucherPaymentCodes } from '../../shared/constants/scheduling.constant';


@Component({
  selector: 'app-appointment-search',
  templateUrl: './appointment-search.component.html',
  styleUrls: ['./appointment-search.component.scss']
})

export class AppointmentSearchComponent implements OnInit {

  /* User defined Public variables  */
  appointmentDetails: AppointmentDetail[];
  showBtns: boolean = false;
  showReschedule: boolean = false;
  showCancel: boolean = false;
  isAllExamValidToCancel: boolean = true;
  isAllValid: boolean = true;
  public apptDetails: AppointmentDetail[] = [];
  public invalidScheduleMethod: boolean = false;
  public invalidReschedulePolicy: boolean = false;
  public overLapStartDate: any;
  public overLapEndDate: any;
  public selectedAppointmentDetails: AppointmentDetail[];
  public isVoucherValid: boolean = true;
  public skipReschedule: boolean = false;
  public cancelNotAllowed: boolean = false;
  public consecutiveDays: boolean;
  public rescheduleErrorMessage: string;
  public batteryConfig: BatteryConfig;

  currentTargetFlow: string = "AppointmentLookup";

  /* Service subscriptions */
  appointmentDetailData$: Subscription;
  appointmentData$: Subscription;
  skipReschedule$: Subscription;
  cancelNotAllowed$: Subscription;
  selectedAppointmentDetails$: Subscription;
  translateLanguage$: Subscription;
  overLapStartDate$: Subscription;
  overLapEndDate$: Subscription;
  batteryConfig$: Subscription;

  /* Input-Output parameter declarations */
  @Output() public selectedAppointments: EventEmitter<AppointmentDetail[]> = new EventEmitter();


  /* Constructor declaration with required injected services */
  constructor(
    private apptService: AppointmentService,
    private metricApiService: MetricApiService,
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private schedulingApiService: SchedulingApiService
  ) {
    this.showReschedule = this.selectedExamDetailService.showReschedule;
    this.showCancel = this.selectedExamDetailService.showCancel;

    this.appointmentDetailData$ = selectedExamDetailService.appointmentDetailData.subscribe(
      appointDetailData => {
        this.appointmentDetails = appointDetailData;
      });

    this.appointmentData$ = selectedExamDetailService.scheduledExams.subscribe(
      scheduledExams => {
        this.apptDetails = scheduledExams;
      });

    this.skipReschedule$ = selectedExamDetailService.skipReschedule.subscribe(
      skipReschedule => {
        this.skipReschedule = skipReschedule;
      });
    
    this.cancelNotAllowed$ = selectedExamDetailService.cancelNotAllowed.subscribe(
      cancelNotAllowed => {
        this.cancelNotAllowed = cancelNotAllowed;
    });

    this.selectedAppointmentDetails$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointmentDetails => {
        this.selectedAppointmentDetails = selectedAppointmentDetails;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.overLapStartDate$ = selectedExamDetailService.overlapsFromDate.subscribe(
      startDate => {
        this.overLapStartDate = startDate;
      });

    this.overLapEndDate$ = selectedExamDetailService.overlapsToDate.subscribe(
      endDate => {
        this.overLapEndDate = endDate;
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.AppointmentSearchComponent)) return;

    this.getAppointmentData(this.appointmentDetails);
    this.selectedExamDetailService.cancelRefundQrTransaction = undefined;
    this.consecutiveDays = this.selectedExamDetailService.consecutiveDays;
  }

  goToReschedule() {
    this.consecutiveDays = this.selectedExamDetailService.consecutiveDays;
    this.apptDetails = this.utilityService.dateSortAsc(this.apptDetails);
    if (this.apptDetails.length) {
      // to grab first scheduled
      this.selectedExamDetailService.setScheduledSlot(
        this.apptDetails[0].appointmentSlot);
    }

    if (this.selectedAppointmentDetails.length > 0) {
      this.selectedAppointmentDetails.forEach(appointment => {
        if (appointment.exam.sessions && appointment.exam.sessions.length) {
          appointment.exam.sessions.forEach((s) => {
            if (this.consecutiveDays) {
              s.selected = true;
            }
            if (s.selected) {
              s.slot = null;
              s.selectedDate = null;
            }
          })
        }
      });
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointmentDetails);
      this.routingService.rescheduleFwdNavigation(ComponentType.AppointmentSearchComponent);
    }

  }

  goToCancel() {
    this.selectedAppointmentDetails.forEach((sa) => {
      if (!!sa.exam.sessions && sa.exam.sessions.length) {
        sa.exam.sessions.forEach(s => s.selected = true);
      }
    });
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointmentDetails);
    this.routingService.cancelFwdNavigation(ComponentType.AppointmentSearchComponent);
  }

  getAppointmentData(event: any) {
    const apptData = event;
    if (this.showReschedule || this.showCancel) this.currentTargetFlow = "Lpi";
    if (apptData && apptData.length) {
      if (apptData.every((appt: any) => {
        return appt.status == "cancelled";
      })) {
        this.showBtns = false;
      }
    }
  }

  /* Call when user is selecting appointment */
  onAppointmentSelection(isAppointmentSelected: boolean): void {
    this.showBtns = (this.showCancel || this.showReschedule) && isAppointmentSelected;
  }

  rescheduleNotAllowedPolicy($event: any) {
    this.invalidReschedulePolicy = $event['error'];
    this.rescheduleErrorMessage = $event['message'];
  }

  isAllExamAllowedToCancelEmit($event: any) {
    this.isAllExamValidToCancel = $event;
  }

  isAllExamValidEmit($event: any) {
    this.isAllValid = $event;
  }

  isScheduleMethodValid($event: any) {
    this.invalidScheduleMethod = $event;
    this.isAllValid = !$event;
  }

  isRescheduleAllowed() {
    return !this.selectedExamDetailService.programNotPublished;
  }

  /* use to set selected appointments in shared service */
  setSelectedAppointments(appointmentDetails: AppointmentDetail[]): void {
    this.selectedExamDetailService.voucherTransactionId = '';

    if (appointmentDetails.length) {
      let voucherPayment = appointmentDetails[0].payments.filter(p => !!p.paymentType && voucherPaymentCodes.indexOf(p.paymentType.toLowerCase()) !== -1);
      if (voucherPayment.length) {
        this.selectedExamDetailService.voucherTransactionId = voucherPayment[0].transactionId;
        this.apptService.validateVoucherPayment
          (appointmentDetails, appointmentDetails[0].candidate, this.overLapStartDate, this.overLapEndDate).subscribe((isAllowed: boolean) => {
            this.isVoucherValid = isAllowed;
          });
      }

      this.getConsents(this.appointmentDetails[0].id);
    }

    this.selectedAppointments.emit();
    this.selectedExamDetailService.setSelectedAppointmentDetails(appointmentDetails);
  }


  /* used to get biometric consent saved on schedule */
  getConsents(confirmationId: string): void {
    this.schedulingApiService.getConsents(confirmationId).subscribe((resp) => {
      if (!!resp.Results[0].Consent) {
        const consent = resp.Results[0].Consent;
        if (!!consent.biometric && consent.biometric.length > 0) {
          if (consent.biometric[0] === 'True') {
            this.selectedExamDetailService.isBiometricConsentSavedInSchedule = true;
          } else {
            this.selectedExamDetailService.isBiometricConsentSavedInSchedule = false;
          }
        }
      }

    });
  }

  canRescheduleInFlow() {
    return !!this.selectedAppointmentDetails && this.selectedAppointmentDetails.every(appt => this.apptService.canScheduleExamInFlow(appt.exam));
  }

  ssoClientEmit($event: boolean) {
    if ($event) {
      this.metricApiService.log(LogLevel.Debug, 'User attempted to reschedule or cancel SSO appointment from LPI flow: User Redirected to error page.');
      this.routingService.invalidNavigation();
    }
  }
}
